import { useState, useEffect } from 'react';
import { photographerService } from '../../../services/photographerService';
import { reviewService } from '../../../services/reviewService';
import { supabase } from '../../../lib/supabase';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import DroneIcon from '../../../components/icons/DroneIcon';
import Button from '../../../components/ui/Button';
import { getShootingMethodLabelAr } from '../../../utils/bookingLabels';

const SHOOTING_METHOD_ICONS = {
  professional_camera: { Icon, name: 'Camera' },
  mobile_camera: { Icon, name: 'Smartphone' },
  drone: { Icon: DroneIcon, name: null },
};

const PhotographerProfileModal = ({ isOpen, onClose, photographerId }) => {
  const [loading, setLoading] = useState(true);
  const [photographerData, setPhotographerData] = useState(null);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [activeTab, setActiveTab] = useState('portfolio');

  useEffect(() => {
    if (isOpen && photographerId) {
      loadPhotographerData();
    }
  }, [isOpen, photographerId]);

  const loadPhotographerData = async () => {
    try {
      setLoading(true);
      
      // Load photographer profile
      const photographer = await photographerService.getPhotographer(photographerId);
      
      if (!photographer) {
        console.error('Photographer not found');
        setLoading(false);
        return;
      }

      // Get equipment from database
      const equipmentData = photographer.equipmentAvailable || photographer.equipment_available || [];
      const equipmentArray = Array.isArray(equipmentData) ? equipmentData : [];
      
      // Get shooting methods
      const shootingMethodsData = photographer.shootingMethods || photographer.shooting_methods || [];
      const shootingMethodsArray = Array.isArray(shootingMethodsData) ? shootingMethodsData : [];
      
      console.log('📸 Shooting methods debug:', {
        photographerId,
        rawData: {
          shootingMethods: photographer.shootingMethods,
          shooting_methods: photographer.shooting_methods
        },
        shootingMethodsData,
        shootingMethodsArray,
        arrayLength: shootingMethodsArray.length
      });

      // Calculate repeat customers percentage
      let repeatCustomersPercentage = 0;
      try {
        const { data: bookingsData } = await supabase
          .from('bookings')
          .select('customer_id')
          .eq('photographer_id', photographerId)
          .eq('status', 'completed');

        if (bookingsData && bookingsData.length > 0) {
          const uniqueCustomers = new Set(bookingsData.map(b => b.customer_id));
          const totalCustomers = uniqueCustomers.size;
          const repeatBookings = bookingsData.length - totalCustomers;
          repeatCustomersPercentage = totalCustomers > 0
            ? Math.round((repeatBookings / bookingsData.length) * 100)
            : 0;
        }
      } catch (err) {
        console.warn('Failed to calculate repeat customers:', err);
      }

      // Format photographer data
      const formattedData = {
        id: photographer.userId || photographer.user_id,
        name: photographer.user?.fullName || photographer.user?.full_name || 'مصور',
        profileImage: photographer.user?.avatarUrl || photographer.user?.avatar_url || 'https://img.rocket.new/generatedImages/rocket_gen_img_1161a7ebd-1763294739154.png',
        profileImageAlt: `صورة ${photographer.user?.fullName || photographer.user?.full_name || 'المصور'}`,
        city: photographer.city || 'الرياض',
        businessName: photographer.businessName || photographer.business_name || '',
        portfolioUrl: photographer.portfolioUrl || photographer.portfolio_url || '',
        experienceLevel: (() => {
          const level = photographer.experienceLevel || photographer.experience_level;
          const years = photographer.experienceYears || photographer.experience_years;
          const levelMap = { certified: 'معتمد', distinguished: 'مميز', elite: 'نخبة', beginner: 'معتمد', intermediate: 'مميز', advanced: 'مميز', expert: 'نخبة' };
          const label = level ? (levelMap[level] || level) : years >= 8 ? 'نخبة' : years >= 4 ? 'مميز' : 'معتمد';
          return label || 'غير محدد';
        })(),
        rating: parseFloat(photographer.rating) || 0,
        reviewCount: photographer.totalReviews || photographer.total_reviews || 0,
        responseTime: photographer.responseTime || photographer.response_time || 2,
        specialties: photographer.specializations || [],
        biography: photographer.bio || 'لا توجد سيرة ذاتية',
        yearsOfExperience: photographer.experienceYears || photographer.experience_years || 0,
        completedBookings: photographer.totalBookings || photographer.total_bookings || 0,
        repeatCustomers: photographer.repeatCustomers || photographer.repeat_customers || repeatCustomersPercentage,
        equipment: equipmentArray,
        shootingMethods: shootingMethodsArray,
        contactEmail: photographer.contact_email || photographer.contactEmail || photographer.user?.email || null,
        contact_email: photographer.contact_email || photographer.contactEmail || photographer.user?.email || null,
        specialSkills: photographer.specialSkills || photographer.special_skills || [],
        languages: photographer.languages || []
      };

      setPhotographerData(formattedData);

      // Load portfolio images
      const { data: portfolioData, error: portfolioError } = await supabase
        .from('portfolio_images')
        .select('*')
        .eq('photographer_id', photographerId)
        .eq('approval_status', 'approved')
        .order('created_at', { ascending: false });

      if (!portfolioError && portfolioData) {
        const formattedPortfolio = portfolioData.map((img) => ({
          id: img.id,
          image: img.image_url,
          imageAlt: img.description || img.title || 'صورة من المعرض',
          title: img.title || 'بدون عنوان',
          category: img.category || 'التصوير الشخصي'
        }));
        setPortfolioItems(formattedPortfolio);
      }

      // Load reviews
      try {
        const reviewsData = await reviewService.getPhotographerReviews(photographerId);
        if (reviewsData && reviewsData.length > 0) {
          const formattedReviews = reviewsData.map((review) => ({
            id: review.id,
            customerName: review.customer?.fullName || review.customer?.full_name || 'عميل',
            customerImage: review.customer?.avatarUrl || review.customer?.avatar_url || 'https://images.unsplash.com/photo-1716816327276-9acb51ed5085',
            customerImageAlt: `صورة ${review.customer?.fullName || review.customer?.full_name || 'العميل'}`,
            rating: review.rating || 5,
            date: review.createdAt || review.created_at || new Date().toISOString(),
            comment: review.comment || review.reviewText || '',
            serviceType: review.serviceType || review.photographyType || 'خدمة التصوير'
          }));
          setReviews(formattedReviews);
        } else {
          setReviews([]);
        }
      } catch (reviewError) {
        console.warn('Failed to load reviews:', reviewError);
        setReviews([]);
      }
    } catch (err) {
      console.error('Failed to load photographer data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'portfolio', label: 'المعرض', icon: 'Image' },
    { id: 'about', label: 'نبذة عني', icon: 'User' },
    { id: 'reviews', label: 'التقييمات', icon: 'Star' }
  ];

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4" dir="rtl">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-2xl font-bold text-slate-900">الملف التعريفي للمصور</h2>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors"
            aria-label="إغلاق"
          >
            <Icon name="X" size={24} className="text-slate-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-slate-600">جاري التحميل...</p>
              </div>
            </div>
          ) : photographerData ? (
            <div className="space-y-6">
              {/* Profile Hero Section */}
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-6">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <Image
                      src={photographerData.profileImage}
                      alt={photographerData.profileImageAlt}
                      className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">{photographerData.name}</h3>
                    {photographerData.businessName && (
                      <p className="text-lg text-slate-600 mb-3">{photographerData.businessName}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                      <div className="flex items-center gap-1.5">
                        <Icon name="MapPin" size={18} className="text-primary" />
                        <span>{photographerData.city}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Icon name="Star" size={18} className="text-warning fill-warning" />
                        <span className="font-semibold">{photographerData.rating}</span>
                        <span className="text-slate-500">({photographerData.reviewCount})</span>
                      </div>
                      {(() => {
                        const level = photographerData.experienceLevel || 'معتمد';
                        const isElite = level.includes('نخبة');
                        const isDist  = level.includes('مميز');
                        const iconName = isElite ? 'Crown' : isDist ? 'Star' : 'BadgeCheck';
                        const badgeCls = isElite
                          ? 'bg-violet-100 text-violet-700 border-violet-300'
                          : isDist
                          ? 'bg-amber-100 text-amber-700 border-amber-300'
                          : 'bg-emerald-100 text-emerald-700 border-emerald-300';
                        return (
                          <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-semibold ${badgeCls}`}>
                            <Icon name={iconName} size={13} />
                            <span>{level}</span>
                          </div>
                        );
                      })()}
                    </div>
                    {photographerData.specialties && photographerData.specialties.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-4">
                        {photographerData.specialties.map((specialty, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-primary/10 text-primary rounded-lg text-sm font-medium"
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-2">
                <div className="flex gap-2">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-smooth ${
                        activeTab === tab.id
                          ? 'bg-primary text-white'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                      }`}
                    >
                      <Icon name={tab.icon} size={20} />
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="mt-6">
                {activeTab === 'portfolio' && (
                  <div>
                    {portfolioItems.length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {portfolioItems.slice(0, 6).map((item) => (
                          <div key={item.id} className="relative aspect-[4/3] overflow-hidden rounded-xl">
                            <Image
                              src={item.image}
                              alt={item.imageAlt}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12 bg-slate-50 rounded-xl">
                        <Icon name="Image" size={48} className="mx-auto mb-4 text-slate-400" />
                        <p className="text-slate-600">لا توجد صور في المعرض</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'about' && (
                  <div className="space-y-6">
                    {/* حقل نبذة عن المصور مخفي — قسم نبذة عني يبقى مع الإحصائيات وباقي المحتوى */}

                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-slate-50 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-slate-900 mb-1">{photographerData.completedBookings}+</div>
                        <p className="text-sm text-slate-600">حجز مكتمل</p>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-slate-900 mb-1">{photographerData.rating}</div>
                        <p className="text-sm text-slate-600">تقييم عام</p>
                      </div>
                    </div>

                    {/* Shooting Methods — نفس التصميم والمكان كما في بطاقة المصور */}
                    {photographerData.shootingMethods && photographerData.shootingMethods.length > 0 && (
                      <div className="bg-slate-50 rounded-xl p-6">
                        <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                          <Icon name="Camera" size={24} className="text-primary" />
                          وسائل التصوير
                        </h4>
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 shadow-sm flex-wrap">
                          {photographerData.shootingMethods.map((method) => {
                            const config = SHOOTING_METHOD_ICONS[method];
                            if (!config) return null;
                            const IconComponent = config.Icon;
                            return (
                              <span
                                key={method}
                                className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/80 text-primary border border-primary/10"
                              >
                                {config.name ? (
                                  <IconComponent name={config.name} size={16} />
                                ) : (
                                  <IconComponent size={16} className="text-primary" />
                                )}
                              </span>
                            );
                          })}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {photographerData.shootingMethods.map((method) => (
                            <span
                              key={method}
                              className="px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-sm font-medium"
                            >
                              {getShootingMethodLabelAr(method)}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}


                    {/* Equipment */}
                    {photographerData.languages && photographerData.languages.length > 0 && (
                      <div className="bg-slate-50 rounded-xl p-6">
                        <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                          <Icon name="Wrench" size={24} className="text-primary" />
                          المعدات المتاحة
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {photographerData.languages.map((language, index) => (
                            <span
                              key={index}
                              className="px-4 py-2 bg-white rounded-lg text-slate-700 border border-slate-200"
                            >
                              {language}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'reviews' && (
                  <div>
                    {reviews.length > 0 ? (
                      <div className="space-y-4">
                        {reviews.map((review) => (
                          <div key={review.id} className="bg-slate-50 rounded-xl p-6">
                            <div className="flex items-start gap-4 mb-3">
                              <Image
                                src={review.customerImage}
                                alt={review.customerImageAlt}
                                className="w-12 h-12 rounded-full object-cover"
                              />
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                  <h5 className="font-semibold text-slate-900">{review.customerName}</h5>
                                  <div className="flex items-center gap-1">
                                    {[...Array(5)].map((_, i) => (
                                      <Icon
                                        key={i}
                                        name="Star"
                                        size={16}
                                        className={i < review.rating ? 'text-warning fill-warning' : 'text-slate-300'}
                                      />
                                    ))}
                                  </div>
                                </div>
                                <p className="text-sm text-slate-600 mb-2">{review.serviceType}</p>
                                {review.comment && (
                                  <p className="text-slate-700">{review.comment}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12 bg-slate-50 rounded-xl">
                        <Icon name="Star" size={48} className="mx-auto mb-4 text-slate-400" />
                        <p className="text-slate-600">لا توجد تقييمات بعد</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-20">
              <Icon name="UserX" size={48} className="mx-auto mb-4 text-slate-400" />
              <p className="text-slate-600">لم يتم العثور على بيانات المصور</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhotographerProfileModal;
